import ReactMarkdown from 'react-markdown';
import type { Components } from 'react-markdown';
import remarkMath from 'remark-math';
import remarkGfm from 'remark-gfm';

const tableWrapperStyle = {
  display: 'block',
  width: '100%',
  overflowX: 'auto',
  marginBottom: '12px',
} as const;

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse' as const,
  borderSpacing: 0,
  fontSize: '0.95rem',
  border: '1px solid #e2e8f0',
};

const theadStyle = {
  background: '#f7fafc',
};

const cellBaseStyle = {
  textAlign: 'left' as const,
  padding: '8px 10px',
  border: '1px solid #e2e8f0',
  verticalAlign: 'top' as const,
  wordBreak: 'break-word' as const,
};

const paragraphStyle = {
  marginBottom: '8px',
  textAlign: 'left' as const,
  width: '100%',
  maxWidth: '100%',
  wordBreak: 'break-word' as const,
  overflowWrap: 'break-word' as const,
};

const headingBase = {
  fontWeight: 700,
  marginBottom: '8px',
  width: '100%',
  maxWidth: '100%',
  wordBreak: 'break-word' as const,
  overflowWrap: 'break-word' as const,
};

const headingSizes = {
  h1: { fontSize: '1.5rem' },
  h2: { fontSize: '1.25rem' },
  h3: { fontSize: '1.125rem' },
} as const;

const listBase = {
  paddingLeft: '16px',
  marginBottom: '8px',
  width: '100%',
  maxWidth: '100%',
} as const;

const listItemStyle = {
  marginBottom: '4px',
  width: '100%',
  maxWidth: '100%',
} as const;

const components: Components = {
  p: ({ children, style, ...rest }) => (
    <p style={{ ...paragraphStyle, ...(style || {}) }} {...rest}>
      {children}
    </p>
  ),
  h1: ({ children, style, ...rest }) => (
    <h1
      style={{ ...headingBase, ...headingSizes.h1, ...(style || {}) }}
      {...rest}
    >
      {children}
    </h1>
  ),
  h2: ({ children, style, ...rest }) => (
    <h2
      style={{ ...headingBase, ...headingSizes.h2, ...(style || {}) }}
      {...rest}
    >
      {children}
    </h2>
  ),
  h3: ({ children, style, ...rest }) => (
    <h3
      style={{ ...headingBase, ...headingSizes.h3, ...(style || {}) }}
      {...rest}
    >
      {children}
    </h3>
  ),
  ul: ({ children, style, ...rest }) => (
    <ul style={{ ...listBase, ...(style || {}) }} {...rest}>
      {children}
    </ul>
  ),
  ol: ({ children, style, ...rest }) => (
    <ol style={{ ...listBase, ...(style || {}) }} {...rest}>
      {children}
    </ol>
  ),
  li: ({ children, style, ...rest }) => (
    <li style={{ ...listItemStyle, ...(style || {}) }} {...rest}>
      {children}
    </li>
  ),
  strong: ({ children, style, ...rest }) => (
    <strong style={{ fontWeight: 700, ...(style || {}) }} {...rest}>
      {children}
    </strong>
  ),
  em: ({ children, style, ...rest }) => (
    <em style={{ fontStyle: 'italic', ...(style || {}) }} {...rest}>
      {children}
    </em>
  ),
  a: ({ children, style, ...rest }) => (
    <a
      style={{ color: '#2563eb', wordBreak: 'break-all', ...(style || {}) }}
      target="_blank"
      rel="noopener noreferrer"
      {...rest}
    >
      {children}
    </a>
  ),
  table: ({ children, ...props }) => (
    <div style={tableWrapperStyle}>
      <table style={tableStyle} {...props}>
        {children}
      </table>
    </div>
  ),
  thead: ({ children, ...props }) => (
    <thead style={theadStyle} {...props}>
      {children}
    </thead>
  ),
  tbody: ({ children, ...props }) => <tbody {...props}>{children}</tbody>,
  tr: ({ children, ...props }) => <tr {...props}>{children}</tr>,
  th: ({ children, style, ...rest }) => (
    <th
      style={{ ...cellBaseStyle, fontWeight: 600, ...(style || {}) }}
      {...rest}
    >
      {children}
    </th>
  ),
  td: ({ children, style, ...rest }) => (
    <td style={{ ...cellBaseStyle, ...(style || {}) }} {...rest}>
      {children}
    </td>
  ),
};
const Markdown = ({ content }: { content: string | null | undefined }) => {
  if (!content) return null;

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm, [remarkMath]]}
      components={components}
    >
      {content}
    </ReactMarkdown>
  );
};

export default Markdown;
